/* AppleCare+ cart-drawer upsell - OPTIMISTIC add/remove.

   Both chip states (offer + added) are rendered in the cart line; the
   .is-offer / .is-added class on .cart-item__applecare picks which one shows.
   On tap we flip that class INSTANTLY (so it feels immediate regardless of
   network), play the micro-animation, then fire the Cart API request in the
   background and let the Section-Rendering re-render reconcile the drawer
   (totals, real line-key, etc.). On failure we revert the class.

   This is what makes it feel fast: the perceived latency is ~0, not the cart
   round-trip (which is the real cost, especially on `shopify theme dev`). */
(function () {
  // Bump on changes; check window.__alasilApplecareCartVersion in the console.
  window.__alasilApplecareCartVersion = 13;
  if (window.__alasilApplecareCartInit) return;
  window.__alasilApplecareCartInit = true;

  function getCart() {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  function sectionsToRender(cart) {
    return cart && typeof cart.getSectionsToRender === 'function'
      ? (cart.getSectionsToRender().map(function (s) { return s.id; }).join(',') || 'cart-drawer,cart-icon-bubble')
      : 'cart-drawer,cart-icon-bubble';
  }

  function reconcile(cart, data) {

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      // source 'cart-items' stops cart-drawer-items doing a second blind re-render.
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-items',
        cartData: data && typeof data.item_count === 'number' ? data : undefined
      });
    }
    if (cart && typeof cart.renderContents === 'function' && data && data.sections) {
      data.skipDrawerOpen = true; // drawer already open - don't replay the open anim
      cart.renderContents(data);
    } else {
      window.location.reload();
    }
  }

  function delay(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }

  function notifyPdpSelection(selected, appleCareVariantId, parentKey) {
    document.dispatchEvent(new CustomEvent('applecare:cart-selection', {
      detail: {
        selected: selected,
        appleCareVariantId: parseInt(appleCareVariantId, 10),
        parentVariantId: parseInt(parentKey, 10)
      }
    }));
  }

  /* ── Add (offer → added), optimistic ─────────────────────────────── */
  document.addEventListener('click', async function (e) {
    const btn = e.target.closest('[data-applecare-offer]');
    if (!btn || btn.dataset.loading === '1') return;
    e.preventDefault();

    const container = btn.closest('.cart-item__applecare');
    const variantId = parseInt(btn.dataset.acVariant, 10);
    const parentKey = btn.dataset.parentKey;
    const qty = parseInt(btn.dataset.qty, 10) || 1;
    if (!variantId || !parentKey || !container) return;

    btn.dataset.loading = '1';

    // Optimistic flip → "added" + the "gift unlocked" celebration, instantly.
    container.classList.remove('is-offer');
    container.classList.add('is-added', 'is-pending');
    notifyPdpSelection(true, variantId, parentKey);
    const addedChip = container.querySelector('.cart-item__applecare-row--added');
    requestAnimationFrame(function () {
      if (addedChip) addedChip.classList.add('cart-item__applecare-row--just-added');
    });

    const cart = getCart();
    const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
    // Let the celebration play fully before the reconcile swaps the DOM.
    const minCelebrate = delay(560);

    try {
      const res = await window.cartMutationFetch(addUrl + '.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: qty, parent_line_key: parentKey }],
          sections: sectionsToRender(cart),
          sections_url: window.location.pathname
        })
      });
      const data = await res.json();

      if (!res.ok || data.status) {
        // Revert the optimistic flip.
        container.classList.remove('is-added', 'is-pending');
        container.classList.add('is-offer');
        notifyPdpSelection(false, variantId, parentKey);
        delete btn.dataset.loading;
        return;
      }

      await minCelebrate;
      reconcile(cart, data);
    } catch (err) {
      container.classList.remove('is-added', 'is-pending');
      container.classList.add('is-offer');
      notifyPdpSelection(false, variantId, parentKey);
      delete btn.dataset.loading;
    }
  });

  /* ── Remove (added → offer), optimistic ──────────────────────────── */
  document.addEventListener('click', async function (e) {
    const rm = e.target.closest('[data-applecare-remove]');
    if (!rm || rm.dataset.loading === '1') return;
    e.preventDefault();
    e.stopPropagation();

    const lineKey = rm.dataset.lineKey;
    const container = rm.closest('.cart-item__applecare');
    const variantId = parseInt(container && container.dataset.acVariant, 10);
    const parentKey = container && container.dataset.parentKey;
    // No key yet = the add hasn't been confirmed by the server; ignore the tap.
    if (!lineKey || !container) return;

    rm.dataset.loading = '1';
    const removingChip = rm.closest('.cart-item__applecare-row');
    if (removingChip) removingChip.classList.add('cart-item__applecare-row--removing');

    // After the short exit animation, flip back to the offer - instantly, well
    // before the network returns.
    const flipped = delay(160).then(function () {
      container.classList.remove('is-added', 'is-pending');
      container.classList.add('is-offer');
      notifyPdpSelection(false, variantId, parentKey);
      if (removingChip) removingChip.classList.remove('cart-item__applecare-row--removing');
    });

    const cart = getCart();
    const changeUrl = (window.routes && window.routes.cart_change_url) || '/cart/change';

    try {
      const res = await window.cartMutationFetch(changeUrl + '.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: 0,
          sections: sectionsToRender(cart),
          sections_url: window.location.pathname
        })
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || 'remove failed');
      await flipped; // offer is already showing; now reconcile totals/state
      reconcile(cart, data);
    } catch (err) {
      // Revert to "added".
      container.classList.remove('is-offer');
      container.classList.add('is-added');
      notifyPdpSelection(true, variantId, parentKey);
      delete rm.dataset.loading;
    }
  });

  /* ── Keep each AppleCare child qty in sync with its parent device ──
     Nested cart lines don't auto-sync quantity (that needs a Cart Transform
     function, not a theme), so we match the child when the device qty changes -
     OPTIMISTICALLY so it follows with no visible lag:
       1. read the new quantities from the cartUpdate payload (no extra /cart.js),
       2. update the chip's "× N" in the DOM immediately,
       3. persist all mismatches in one background /cart/update.js, then reconcile.
     The mismatch check is its own loop guard; the in-flight flag prevents re-entry. */
  let acSyncing = false;

  function setChipQty(parentKey, qty) {
    const cont = document.querySelector(
      '.cart-item__applecare[data-parent-key="' + (window.CSS && CSS.escape ? CSS.escape(parentKey) : parentKey) + '"]'
    );
    if (!cont) return;
    const sub = cont.querySelector('.cart-item__applecare-row--added .cart-item__applecare-sub');
    if (!sub) return;
    let qtyEl = sub.querySelector('.cart-item__applecare-qty');
    if (qty > 1) {
      if (!qtyEl) {
        sub.appendChild(document.createTextNode(' '));
        qtyEl = document.createElement('span');
        qtyEl.className = 'cart-item__applecare-qty';
        sub.appendChild(qtyEl);
      }
      qtyEl.textContent = '× ' + qty;
    } else if (qtyEl) {
      qtyEl.remove();
    }
  }

  async function syncAppleCareQty(eventData) {
    if (acSyncing) return;

    let items = eventData && eventData.cartData && eventData.cartData.items;
    if (!items) {
      try {
        if (typeof window.getCartSnapshot !== 'function') return;
        items = (await window.getCartSnapshot())?.items;
      } catch (e) { return; }
    }
    items = items || [];

    const byKey = {};
    items.forEach(function (it) { byKey[it.key] = it; });

    const updates = {};
    let any = false;
    items.forEach(function (it) {
      const pk = it.parent_relationship && it.parent_relationship.parent_key;
      const parent = pk && byKey[pk];
      if (parent && it.quantity !== parent.quantity) {
        updates[it.key] = parent.quantity;
        setChipQty(pk, parent.quantity); // optimistic - instant, in sync with the device
        any = true;
      }
    });
    if (!any) return;

    acSyncing = true;
    const cart = getCart();
    const updateUrl = (window.routes && window.routes.cart_update_url) || '/cart/update';
    try {
      const res = await window.cartMutationFetch(updateUrl + '.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          updates: updates,
          sections: sectionsToRender(cart),
          sections_url: window.location.pathname
        })
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || 'quantity sync failed');
      reconcile(cart, data);
    } catch (e) {
      // The optimistic chip quantity must not remain wrong indefinitely. The
      // drawer runtime coalesces this with any other pending refresh and
      // restores the server-authoritative parent/child quantities.
      if (typeof window.refreshCartDrawerState === 'function') {
        await window.refreshCartDrawerState({ skipDrawerOpen: true });
      }
    }
    // Release after the reconcile's own cartUpdate has fired (and been skipped).
    setTimeout(function () { acSyncing = false; }, 80);
  }

  (function bindSync() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (ev) { syncAppleCareQty(ev); });
    } else {
      setTimeout(bindSync, 300); // pub-sub not ready yet
    }
  })();
})();
